import { put, call, select, takeLatest, takeEvery } from "redux-saga/effects";
import * as actions from "./actions";
import { Endpoints } from "services/api";
import errorHandler from "common/errorHandler";
import _ from "lodash";

const getTenant = (state) => state.userLogin.tenant;
const getUser = (state) => state.userLogin.user;

// --- catalog ---
function* getCatalog(api) {
  const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/providers/catalog`);
  return response.data;
}
export function* getProviderCatalogRequest(api) {
  try {
    const catalog = yield call(getCatalog, api);
    yield put(actions.getProviderCatalogSuccess(catalog));
  } catch (error) {
    yield put(actions.getProviderCatalogFailure(errorHandler(error, "Get", "provider catalog")));
  }
}

// --- configured providers ---
function* getProviders(api) {
  const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/providers`);
  return response.data;
}
export function* getProvidersRequest(api) {
  try {
    const providers = yield call(getProviders, api);
    yield put(actions.getProvidersSuccess(providers));
  } catch (error) {
    yield put(actions.getProvidersFailure(errorHandler(error, "Get", "providers")));
  }
}

export function* upsertProviderRequest(api, { payload }) {
  try {
    const response = yield call([api, api.put], `${Endpoints.aiGateway}/api/v1/providers/${payload.provider}`, payload.config);
    yield put(actions.upsertProviderSuccess(response.data));
  } catch (error) {
    yield put(actions.upsertProviderFailure(errorHandler(error, "Save", "provider")));
  }
}

export function* deleteProviderRequest(api, { payload }) {
  try {
    yield call([api, api.delete], `${Endpoints.aiGateway}/api/v1/providers/${payload.provider}`);
    yield put(actions.deleteProviderSuccess(payload.provider));
  } catch (error) {
    yield put(actions.deleteProviderFailure(errorHandler(error, "Delete", "provider")));
  }
}

// --- model bindings ---
function* getBindings(api) {
  const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/model-bindings`);
  return response.data;
}
export function* getModelBindingsRequest(api) {
  try {
    const bindings = yield call(getBindings, api);
    yield put(actions.getModelBindingsSuccess(bindings));
  } catch (error) {
    yield put(actions.getModelBindingsFailure(errorHandler(error, "Get", "model bindings")));
  }
}

export function* upsertModelBindingRequest(api, { payload }) {
  try {
    const response = yield call([api, api.put], `${Endpoints.aiGateway}/api/v1/model-bindings/${payload.capability}`, payload.binding);
    yield put(actions.upsertModelBindingSuccess(response.data));
  } catch (error) {
    yield put(actions.upsertModelBindingFailure(errorHandler(error, "Save", "model binding")));
  }
}

export function* deleteModelBindingRequest(api, { payload }) {
  try {
    yield call([api, api.delete], `${Endpoints.aiGateway}/api/v1/model-bindings/${payload.capability}`);
    yield put(actions.deleteModelBindingSuccess(payload.capability));
  } catch (error) {
    yield put(actions.deleteModelBindingFailure(errorHandler(error, "Delete", "model binding")));
  }
}

// --- live provider models ---
export function* getProviderModelsRequest(api, { payload }) {
  try {
    const response = yield call([api, api.get], `${Endpoints.aiGateway}/api/v1/providers/${payload.provider}/models`);
    const data = response.data || {};
    yield put(actions.getProviderModelsSuccess(payload.provider, data.Items || [], data.source));
  } catch (error) {
    yield put(actions.getProviderModelsFailure(payload.provider, errorHandler(error, "Get", "provider models")));
  }
}

// --- permission (resource: aigateway) ---
export function* getAiGatewayPermissionRequest(api) {
  const permission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
  try {
    const tenant = yield select(getTenant);
    const user = yield select(getUser);
    const response = yield call(
      [api, api.get],
      `${Endpoints.permissions}/api/v1/users/${user.userId}/permissions/resources/aigateway`,
      { tenantid: tenant.tenantId }
    );
    if (response && response.data && response.data.Items && response.data.Items.length > 0) {
      let permissionActions = response.data.Items.map((item) => item.actions);
      permissionActions = _.union(...permissionActions);
      permission.canAdd = permissionActions.includes("add");
      permission.canView = permissionActions.includes("view");
      permission.canEdit = permissionActions.includes("edit");
      permission.canDelete = permissionActions.includes("delete");
    }
    yield put(actions.getAiGatewayPermissionSuccess(permission));
  } catch (error) {
    yield put(actions.getAiGatewayPermissionFailure(errorHandler(error, "Get", "ai gateway permission")));
  }
}

export default function* ({ api }) {
  yield takeLatest(actions.GET_PROVIDER_CATALOG_REQUEST, getProviderCatalogRequest, api);
  yield takeLatest(actions.GET_PROVIDERS_REQUEST, getProvidersRequest, api);
  yield takeLatest(actions.UPSERT_PROVIDER_REQUEST, upsertProviderRequest, api);
  yield takeLatest(actions.DELETE_PROVIDER_REQUEST, deleteProviderRequest, api);
  yield takeLatest(actions.GET_MODEL_BINDINGS_REQUEST, getModelBindingsRequest, api);
  yield takeLatest(actions.UPSERT_MODEL_BINDING_REQUEST, upsertModelBindingRequest, api);
  yield takeLatest(actions.DELETE_MODEL_BINDING_REQUEST, deleteModelBindingRequest, api);
  yield takeEvery(actions.GET_AI_GATEWAY_PERMISSION_REQUEST, getAiGatewayPermissionRequest, api);
  yield takeEvery(actions.GET_PROVIDER_MODELS_REQUEST, getProviderModelsRequest, api);
}
